import { useEffect, useState, useRef } from 'react';
import { FiArrowRight, FiX } from 'react-icons/fi';

import { questionsApi } from '@/services/questions';
import { Button, ButtonIcon } from '@/components/ui/button';
import PresetCards from './PresetCards';
import QuestionsFilters from './QuestionsFilters';

// Custom easing curves (Emil-style: stronger than default CSS ease-out).
// Used via Tailwind arbitrary values: ease-[var(--ease-out)] or inline.
const EASE_OUT = 'cubic-bezier(0.23, 1, 0.32, 1)';

/**
 * GATE de seleção: presets + filtros custom + contador live + CTA Ver questões.
 * Click no CTA → onApply(filters) (parent muda phase).
 */
export default function QuestionsGate({ taxonomies, onApply }) {
  const [filters, setFilters] = useState({});
  const [count, setCount] = useState(null);
  const [loadingCount, setLoadingCount] = useState(false);
  const [activePreset, setActivePreset] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoadingCount(true);
    debounceRef.current = setTimeout(() => {
      questionsApi.count(filters)
        .then(json => setCount(json?.count ?? 0))
        .catch(() => setCount(null))
        .finally(() => setLoadingCount(false));
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [filters]);

  const handlePresetSelect = preset => {
    setFilters(preset.filters);
    setActivePreset(preset.id);
  };

  const handleFiltersChange = next => {
    setFilters(next);
    setActivePreset(null);
  };

  const clearAll = () => {
    setFilters({});
    setActivePreset(null);
  };

  const hasFilters = Object.keys(filters).length > 0;
  const canApply = count !== null && count > 0;

  return (
    <div className="space-y-8">
      {/* Page header */}
      <header>
        <h2 className="text-xl font-bold text-foreground">Banco de questões</h2>
        <p className="mt-1 text-sm text-neutral">
          Escolha um preset ou ajuste filtros antes de explorar a lista.
        </p>
      </header>

      {/* Presets */}
      <section>
        <div className="mb-3 flex items-baseline justify-between">
          <h3 className="text-sm font-semibold text-foreground">Atalhos</h3>
          <span className="text-xs text-neutral-light">
            Click pra preencher os filtros
          </span>
        </div>
        <PresetCards onSelect={handlePresetSelect} activeId={activePreset} />
      </section>

      {/* Filtros */}
      <section className="rounded-xl border border-neutral-lightest bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-baseline justify-between">
          <h3 className="text-sm font-semibold text-foreground">Filtros</h3>
          {hasFilters && (
            <button
              type="button"
              onClick={clearAll}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-neutral transition-[color,background-color] duration-150 hover:bg-neutral-lightest hover:text-foreground active:scale-[0.97]"
              style={{ transitionTimingFunction: EASE_OUT }}
            >
              <FiX className="size-3" />
              Limpar
            </button>
          )}
        </div>
        <QuestionsFilters
          filters={filters}
          onChange={handleFiltersChange}
          taxonomies={taxonomies}
        />
      </section>

      {/* Resultado + CTA */}
      <section
        className="rounded-xl border border-primary/20 bg-gradient-to-br from-primary/[0.04] to-primary/[0.02] p-5 shadow-sm"
      >
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <CountIndicator count={count} loading={loadingCount} />
          <Button
            disabled={!canApply}
            onClick={() => onApply(filters)}
            className="active:scale-[0.97] transition-transform duration-150"
            style={{ transitionTimingFunction: EASE_OUT }}
            title={!canApply ? 'Nenhuma questão bate com os filtros atuais' : ''}
          >
            Ver questões
            <ButtonIcon><FiArrowRight /></ButtonIcon>
          </Button>
        </div>
      </section>
    </div>
  );
}

/**
 * Indicador de contagem com transição entre loading (pulse) e number (fade).
 * tabular-nums faz o number "settled" sem dança de largura.
 */
function CountIndicator({ count, loading }) {
  if (loading && count === null) {
    return (
      <div className="flex items-center gap-3">
        <span className="inline-block h-3 w-20 animate-pulse rounded-full bg-neutral-lightest" />
        <span className="text-sm text-neutral">contando questões…</span>
      </div>
    );
  }

  if (count === null) {
    return <span className="text-sm text-neutral-light">—</span>;
  }

  if (count === 0) {
    return (
      <span className="text-sm text-neutral">
        Nenhuma questão bate com os filtros atuais
      </span>
    );
  }

  return (
    <div className="flex items-baseline gap-2">
      <span
        key={count}
        className="tabular-nums text-3xl font-bold leading-none text-primary-dark"
        style={{ animation: `count-bump 200ms cubic-bezier(0.23, 1, 0.32, 1)` }}
      >
        {count.toLocaleString('pt-BR')}
      </span>
      <span className="text-sm text-neutral">
        {count === 1 ? 'questão encontrada' : 'questões encontradas'}
        {loading && (
          <span className="ml-2 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary/60 align-middle" />
        )}
      </span>
    </div>
  );
}

// Animation keyframe via style tag (so it's scoped within this file context)
// Inject once
if (typeof window !== 'undefined' && !document.getElementById('count-bump-kf')) {
  const s = document.createElement('style');
  s.id = 'count-bump-kf';
  s.textContent = `@keyframes count-bump { 0% { opacity: 0.4; transform: translateY(2px); } 100% { opacity: 1; transform: translateY(0); } }`;
  document.head.appendChild(s);
}
